import AACChromeAPI from '@amzn/aac-chrome-api';
import { getInternalChromeConfig } from 'src/commons/apis';
import { isFromTrim, shouldLoadSystemJsAssets } from 'src/commons/utils';
import { logger } from 'src/commons/utils/logger';

// Milliseconds of main-thread quiet (no long task) that marks the interactive
// idle window. Matches the 5 x 100ms window used by the RUM TTI algorithm.
const HMH_QUIET_WINDOW_MS = 500;
// Absolute cap so HMH still loads even if the page never goes fully quiet.
const HMH_MAX_DEFER_MS = 12000;

/**
 * Loads HMH once the main thread has been free of long tasks for
 * HMH_QUIET_WINDOW_MS, observed via PerformanceObserver 'longtask'. This lands
 * HMH's eval after the interactive quiet window instead of letting it become
 * the last long task before TTI. Falls back to requestIdleCallback / setTimeout
 * where the longtask API is unavailable, and always loads by HMH_MAX_DEFER_MS.
 */
const loadHmhWhenIdle = (doLoad: () => void): void => {
  const runFallback = () => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(doLoad);
    } else {
      setTimeout(doLoad, 1);
    }
  };

  if (
    typeof window === 'undefined' ||
    typeof (window as any).PerformanceObserver === 'undefined'
  ) {
    runFallback();
    return;
  }

  const timers: {
    quiet?: ReturnType<typeof setTimeout>;
    deadline?: ReturnType<typeof setTimeout>;
  } = {};
  let fired = false;
  let observer: PerformanceObserver | undefined;

  const fire = () => {
    if (fired) return;
    fired = true;
    clearTimeout(timers.quiet);
    clearTimeout(timers.deadline);
    observer?.disconnect();
    doLoad();
  };

  const armQuietWindow = () => {
    clearTimeout(timers.quiet);
    timers.quiet = setTimeout(fire, HMH_QUIET_WINDOW_MS);
  };

  try {
    observer = new PerformanceObserver(() => {
      // A long task ran: reset the window and wait for the next quiet gap.
      if (!fired) armQuietWindow();
    });
    observer.observe({ type: 'longtask', buffered: true } as any);
  } catch {
    runFallback();
    return;
  }

  // Start the first quiet window; if no long task fires within the window, load.
  armQuietWindow();
  timers.deadline = setTimeout(fire, HMH_MAX_DEFER_MS);
};

/**
 * Defers HMH bundle loading to after the page's interactive idle window,
 * keeping it off the critical rendering path (TTI/LCP) while preserving
 * auto-show tooltips. Registers a ChromeConfigurationChange listener after
 * initial load.
 */
export const loadHmhDeferred = (): (() => void) | void => {
  const load = () => {
    if ((window as any).hmh_loaded) return;
    const doLoad = async () => {
      await loadHmhBundleScript(undefined, true);
      AACChromeAPI.getCommonEventTarget().addEventListener(
        'ChromeConfigurationChange',
        (e) => loadHmhBundleScript(e, true),
      );
    };
    const hasActiveChat = !!window.localStorage.getItem(
      'ADS_AGENT_CURRENT_CHAT_ID',
    );
    if (hasActiveChat) {
      doLoad();
    } else {
      loadHmhWhenIdle(doLoad);
    }
  };
  if (document.readyState === 'complete') {
    load();
  } else {
    window.addEventListener('load', load);
    return () => window.removeEventListener('load', load);
  }
};

const loadHmh = (hmhUrl: string) => {
  if (shouldLoadSystemJsAssets()) {
    window.System.import(hmhUrl);
  } else {
    // System.import is not a function on Rodeo so need a backup way of loading the script
    const scriptEle = document.createElement('script');
    scriptEle.setAttribute('src', hmhUrl);
    scriptEle.setAttribute('type', 'text/javascript');
    scriptEle.setAttribute('id', 'hmh-bundle');
    scriptEle.defer = true;
    document.head.appendChild(scriptEle);
  }
};

/**
 * @param isDeferredLoad true when called from the deferred loader (Footer idle
 * window, help-button click). The server does not inject HMH on Trim pages, so
 * the deferred loader loads it there too, using the standalone bundle, and
 * regardless of the page's hmhEnabled chrome config. The legacy
 * ChromeConfigurationChange listener registered in index.tsx keeps the
 * original behavior: hmhEnabled pages only, and never on Trim.
 */
export const loadHmhBundleScript = async (
  e?: any,
  isDeferredLoad?: boolean,
) => {
  const chromeConfig =
    e?.detail?.chromeConfiguration || AACChromeAPI.getChromeConfiguration();
  const hmhEnabled: boolean =
    chromeConfig.getHmhEnabled &&
    typeof chromeConfig.getHmhEnabled === 'function'
      ? chromeConfig.getHmhEnabled()
      : false;
  // todo: clean this up as HMH is fully dialed up and the script can be printed on the server side
  if (hmhEnabled || isDeferredLoad) {
    // HMH can get loaded from the backend and this check ensures it has not been
    // TODO: Remove this check after the backend import is removed
    // sim: https://sim.amazon.com/issues/f7264960-d007-4933-9545-21064ca6f29c
    const hmhScriptExists = !!document.getElementById('hmh-bundle');
    if (!hmhScriptExists && (isDeferredLoad || !isFromTrim())) {
      try {
        const internalChromeConfig = await getInternalChromeConfig();
        const hmhFeatureInfo =
          internalChromeConfig.featureLaunchInfo?.features.find(
            (feature) => feature.name === 'HMH' && feature.isEnabled,
          );
        // onLoad event is to ensure the DOM and window have finished loading before attempting to load HMH.
        const hmhUrl =
          isDeferredLoad &&
          isFromTrim() &&
          hmhFeatureInfo?.content?.standaloneUrl
            ? hmhFeatureInfo.content.standaloneUrl
            : hmhFeatureInfo?.content?.url;
        if (hmhUrl) {
          if (document.readyState === 'complete') {
            loadHmh(hmhUrl);
          } else {
            window.addEventListener('load', () => {
              loadHmh(hmhUrl);
            });
          }
        }
      } catch (e: any) {
        logger.error('failed to fetch chromeData:', e);
      }
    }

    AACChromeAPI.getCommonEventTarget().removeEventListener(
      'ChromeConfigurationChange',
      loadHmhBundleScript,
    );
  }
};
